// Aktualizacja aplikacji ze sklepu (docs/pwa/NATYWNA.md 5b, 5c) - przy starcie i powrocie do aplikacji, najwyżej co
// 10 min:
// - część natywna: własny numer wersji (versionCode z `npm run apk` w telefon/) porównany z `/pobierz/android/wersja`
//   Workera APP. Nowsza - baner nad ekranami („Pobierz” otwiera link w przeglądarce telefonu); poniżej minimalnej -
//   ekran blokujący zamiast APP. Aplikacja ze Sklepu Play (wtyczka `Wydanie`, build `npm run sklep`) według zasad Play
//   aktualizuje się tylko przez Play - bez banera, ekran blokujący prowadzi do Play (numery obu kanałów z jednego ciągu,
//   więc ta sama minimalna); starsza aplikacja bez wtyczki = wersja testowa;
// - paczka APP (ekrany, logika, wygląd): `/aplikacja/paczka.json` (scripts/paczka-aplikacji.mjs) - inna niż bieżąca =
//   pobranie w tle (`@capgo/capacitor-updater`, suma sha256) i podmiana przy następnym zejściu aplikacji w tło.
//   Aplikacja poniżej minimalnej paczki nie dostaje (nowa paczka może wymagać nowej części natywnej). Paczka, która
//   po starcie nie zgłosi gotowości (potwierdzStart), wtyczka cofa do poprzedniej.
// Przeglądarka i iPhone (App Store sam aktualizuje; paczki na iPhonie - później) - nic.
import { useEffect, useState, useSyncExternalStore } from "react";
import { App } from "@capacitor/app";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { adres } from "../api/adres";
import { t } from "../i18n/t";
import { IkonaPobierz } from "./ikony";
import { natywna } from "./instalacja";
import type { BanerDane } from "./Powloka";
import { Przycisk } from "./Przyciski";
import { Pusto } from "./Stany";

type StanAktualizacji = "aktualna" | "nowa" | "wymagana";

const CO_ILE_MS = 10 * 60_000;
const PLAY = "https://play.google.com/store/apps/details?id=pl.termometrwifi.aplikacja";
const Wydanie = registerPlugin<{ stan(): Promise<{ sklep: boolean }> }>("Wydanie");
let stan: StanAktualizacji = "aktualna";
let ostatnio = 0;
let zeSklepu = false;
const sluchacze = new Set<() => void>();

async function sprawdz(): Promise<void> {
  if (!natywna() || Capacitor.getPlatform() !== "android" || Date.now() - ostatnio < CO_ILE_MS) return;
  ostatnio = Date.now();
  try {
    const [info, odp, wydanie] = await Promise.all([App.getInfo(), fetch(adres("/pobierz/android/wersja"), { cache: "no-store" }),
      Wydanie.stan().catch(() => ({ sklep: false }))]);
    zeSklepu = wydanie.sklep;
    if (!odp.ok) return;
    const w = (await odp.json()) as { najnowsza: number; minimalna: number };
    const moja = Number(info.build) || 0;
    const nowy: StanAktualizacji = moja < w.minimalna ? "wymagana" : !zeSklepu && moja < w.najnowsza ? "nowa" : "aktualna";
    if (nowy !== stan) {
      stan = nowy;
      for (const f of sluchacze) f();
    }
    if (nowy !== "wymagana") await paczka();
  } catch { /* bez sieci - sprawdzi przy następnym powrocie */ }
}

let pobierana = "";

/** Nowa paczka APP z serwera - pobrana w tle, wchodzi przy następnym zejściu aplikacji w tło. */
async function paczka(): Promise<void> {
  const [odp, biezaca] = await Promise.all([fetch(adres("/aplikacja/paczka.json"), { cache: "no-store" }), CapacitorUpdater.current()]);
  if (!odp.ok) return;
  const p = (await odp.json()) as { wersja: string; zip: string; sha256: string };
  // Paczka wbudowana nie zna swojej wersji we wtyczce („builtin”) - jej paczka.json leży w niej (telefon/paczka.mjs).
  const moja = biezaca.bundle.version === "builtin"
    ? ((await (await fetch("/aplikacja/paczka.json")).json()) as { wersja: string }).wersja
    : biezaca.bundle.version;
  if (moja === p.wersja || pobierana === p.wersja) return;
  pobierana = p.wersja;
  const b = await CapacitorUpdater.download({ url: adres(p.zip), version: p.wersja, checksum: p.sha256 });
  await CapacitorUpdater.next({ id: b.id });
}

/** Wersja aplikacji ze sklepu do Ustawień: numer części natywnej i paczki APP („wbudowana” - z instalacji); null -
 *  przeglądarka. Widać, czy nowa paczka już doszła (zgłoszenia błędów). */
export function useWersjaAplikacji(): string | null {
  const [w, ustawW] = useState<string | null>(null);
  useEffect(() => {
    if (!natywna()) return;
    void Promise.all([App.getInfo(), CapacitorUpdater.current()]).then(([info, biezaca]) => ustawW(
      t("Wersja aplikacji {n} · paczka {p}", { n: info.build, p: biezaca.bundle.version === "builtin" ? t("wbudowana") : biezaca.bundle.version })),
    () => undefined);
  }, []);
  return w;
}

/** Po pierwszym narysowaniu APP (Aplikacja.tsx): ta paczka działa - wtyczka nie cofnie jej do poprzedniej. */
export function potwierdzStart(): void {
  if (natywna()) void CapacitorUpdater.notifyAppReady();
}

void sprawdz();
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void sprawdz(); });

const pobierz = () => void window.open(zeSklepu ? PLAY : adres("/pobierz/android"), "_blank");

export function useAktualizacja(): StanAktualizacji {
  return useSyncExternalStore((f) => { sluchacze.add(f); return () => sluchacze.delete(f); }, () => stan);
}

/** Baner powłoki (obok serwisu, zaproszeń i oczekujących): jest nowsza wersja aplikacji. */
export function useBaneryAktualizacji(): BanerDane[] {
  return useAktualizacja() === "nowa" ? [{ id: "aktualizacja", rodzaj: "info", ikona: IkonaPobierz,
    tresc: t("Dostępna nowa wersja aplikacji."), etykietaAkcji: t("Pobierz"), onAkcja: pobierz }] : [];
}

/** Ekran zamiast APP: ta wersja aplikacji nie działa już z serwerem. */
export function EkranAktualizacji() {
  return (
    <div className="iot-app iot-start">
      <Pusto ikona={IkonaPobierz} tytul={t("Zaktualizuj aplikację")}
        opis={t("Ta wersja aplikacji jest już nieaktualna. Pobierz nową, żeby dalej korzystać z urządzeń.")}
        akcje={<Przycisk wariant="glowny" ikona={IkonaPobierz} onClick={pobierz}>{t("Pobierz nową wersję")}</Przycisk>} />
    </div>
  );
}
